"use client";

import { useActionState, useState } from "react";
import { Button } from "@/template/ui-kit/button";
import { Input } from "@/template/ui-kit/input";
import { Label } from "@/template/ui-kit/label";
import { Alert, AlertDescription } from "@/template/ui-kit/alert";
import { loginAction, type AuthState } from "@/core/actions/auth";
import { Eye, EyeOff } from "lucide-react";
import {
  emptySubmittedValues,
  keepSubmittedValues,
} from "@/template/lib/keep-submitted-values";

// Lo que vuelve como valor por defecto tras un error. La contraseña NO.
const KEPT_FIELDS = ["email"] as const;

export function LoginForm({ next }: { next?: string }) {
  // React resetea el formulario después de cada envío por `action`: con un
  // error ("Email o contraseña incorrectos.") el email quedaba vacío. Lo
  // enviado vuelve como `defaultValue` (mismo patrón que signup-form, #46).
  const [submitted, setSubmitted] = useState(() =>
    emptySubmittedValues(KEPT_FIELDS),
  );
  const [state, formAction, isPending] = useActionState<AuthState, FormData>(
    keepSubmittedValues(KEPT_FIELDS, setSubmitted, loginAction),
    null,
  );
  const [showPassword, setShowPassword] = useState(false);

  return (
    <form action={formAction} className="flex flex-col gap-4">
      {next ? <input type="hidden" name="next" value={next} /> : null}

      <div className="grid gap-2">
        <Label htmlFor="email">Email</Label>
        <Input
          id="email"
          name="email"
          type="email"
          autoComplete="email"
          required
          defaultValue={submitted.email}
          aria-invalid={!!state?.fieldErrors?.email}
        />
        {state?.fieldErrors?.email ? (
          <p className="text-destructive text-sm">
            {state.fieldErrors.email[0]}
          </p>
        ) : null}
      </div>

      <div className="grid gap-2">
        <Label htmlFor="password">Contraseña</Label>
        <div className="relative">
          <Input
            id="password"
            name="password"
            type={showPassword ? "text" : "password"}
            autoComplete="current-password"
            required
            className="pr-10"
            aria-invalid={!!state?.fieldErrors?.password}
          />
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            className="text-muted-foreground hover:text-foreground absolute top-1/2 right-3 -translate-y-1/2"
            aria-label={
              showPassword ? "Ocultar contraseña" : "Mostrar contraseña"
            }
          >
            {showPassword ? (
              <EyeOff className="h-4 w-4" />
            ) : (
              <Eye className="h-4 w-4" />
            )}
          </button>
        </div>
        {state?.fieldErrors?.password ? (
          <p className="text-destructive text-sm">
            {state.fieldErrors.password[0]}
          </p>
        ) : null}
      </div>

      {state?.formError ? (
        <Alert variant="danger">
          <AlertDescription>{state.formError}</AlertDescription>
        </Alert>
      ) : null}

      <Button type="submit" disabled={isPending}>
        {isPending ? "Ingresando…" : "Iniciar sesión"}
      </Button>
    </form>
  );
}
